import { useEffect, useState } from 'react';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { subMinutes, subYears } from 'date-fns';
import { transactionService } from '../../../../channels/secure-web/services';
import { DownloadTypes } from '../../../content-composer/src/components/Generic/FileDownload/FileDownload';
import { useConsolidatedTransactionListFilters } from '../contexts/ConsolidatedTransactions/hooks';
import { useSelectedCustomerId } from '../../../userProfile/src/contexts/UserInfo/hooks';
import { downloadFile, mappingOrderDirection } from '../../../content-composer/src/utils';

export type ConsolidatedTransactionsToDownload = {
  type: DownloadTypes;
  meta: {
    timestamp: string;
  };
};

const useDownloadConsolidatedTransactions = () => {
  const [consolidatedTransactionsToDownload, setConsolidatedTransactionsToDownload] =
    useState<Nullable<ConsolidatedTransactionsToDownload>>(null);

  const { t } = useTranslation();
  const customerId = useSelectedCustomerId();
  const consolidatedTransactionListFilters = useConsolidatedTransactionListFilters();

  const { data: consolidatedTransactionsDownloadData, isLoading: isLoadingConsolidatedTransactionsDownloadData } =
    useInvokeSWRService(
      transactionService.GetConsolidatedTransactionsPrint,
      {
        ...consolidatedTransactionListFilters,
        type: consolidatedTransactionsToDownload?.type,
        fromTrxDate: consolidatedTransactionListFilters?.fromTrxDate
          ? consolidatedTransactionListFilters.fromTrxDate
          : stringToDate({ date: subYears(new Date(), 1).toISOString(), formatExp: "yyyy-MM-dd'T'00:00:00" }),
        toTrxDate: consolidatedTransactionListFilters?.toTrxDate
          ? consolidatedTransactionListFilters.toTrxDate
          : stringToDate({
              date: subMinutes(new Date(), 1).toISOString(),
              formatExp: "yyyy-MM-dd'T'HH:mm:00",
              tz: 'UTC',
            }),
        valueDateFrom: consolidatedTransactionListFilters?.valueDateFrom,
        valueDateTo: consolidatedTransactionListFilters?.valueDateTo,
        beneficiaryName: consolidatedTransactionListFilters?.beneficiaryName,
        fromAmount: consolidatedTransactionListFilters?.fromAmount ?? '0.00',
        toAmount: consolidatedTransactionListFilters?.toAmount ?? '9999999999.99',
        accountNumber: consolidatedTransactionListFilters?.account?.accountNumber ?? undefined,
        customerId,
        orderDirection: mappingOrderDirection(t, consolidatedTransactionListFilters?.orderDirection),
        meta: consolidatedTransactionsToDownload?.meta,
      },
      Boolean(consolidatedTransactionsToDownload),
    );

  useEffect(() => {
    if (consolidatedTransactionsDownloadData && consolidatedTransactionsToDownload) {
      downloadFile(
        t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.Heading'),
        consolidatedTransactionsDownloadData.report,
        consolidatedTransactionsToDownload.type,
      );
      setConsolidatedTransactionsToDownload(null);
    }
  }, [consolidatedTransactionsDownloadData, consolidatedTransactionsToDownload, t]);

  const onDownloadConsolidatedTransactions = (type: DownloadTypes) => {
    setConsolidatedTransactionsToDownload({ type, meta: { timestamp: new Date().toISOString() } });
  };

  return { isLoadingConsolidatedTransactionsDownloadData, onDownloadConsolidatedTransactions };
};

export { useDownloadConsolidatedTransactions };
